import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { appMessage } from '@/utils/appMessage';
import { BizAppConfig } from 'config/application.manifest';
import { unwrapBffResponse } from './services/response-envelope';
import { isManagedOAuthRequest } from './services/managed-oauth-request';
import { normalizeRequestError } from './services/normalize-request-error';
import {
  clearOAuth2Tokens,
  ensureValidAccessToken,
  getAccessToken,
  redirectToSSO,
} from './services/oauth2';

// 与后端约定的响应数据格式
interface ResponseStructure {
  success?: boolean;
  code?: number | string;
  msg?: string;
  data: any;
  errorCode?: number;
  errorMessage?: string;
}

type RequestErrorLike = {
  name?: string;
  message?: string;
  info?: {
    errorCode?: number;
    errorMessage?: string;
  };
  response?: {
    status?: number;
    data?: any;
    config?: { url?: string };
  };
  request?: unknown;
  config?: { url?: string };
  handled?: boolean;
};

const LOGIN_PATH = '/user/login';
const AUTH_ENDPOINTS_SKIP_REFRESH = [
  '/bff/auth/refreshToken',
  '/bff/auth/codeLogin',
  '/bff/auth/logout',
];
const AUTH_ENDPOINTS_SKIP_AUTH_HEADER = [
  '/bff/auth/refreshToken',
  '/bff/auth/codeLogin',
];
const AUTH_ENDPOINTS_ALLOW_MISSING_TOKEN = ['/bff/auth/logout'];
const CURRENT_TENANT_UUID_STORAGE_KEY = 'currentTenantUuid';

const getRequestPathname = (url?: string) => {
  if (!url) return '';

  try {
    const base =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'http://localhost';
    return new URL(url, base).pathname;
  } catch {
    return url;
  }
};

const matchesAuthEndpoint = (url: string | undefined, endpoints: string[]) => {
  const pathname = getRequestPathname(url);
  return endpoints.some((endpoint) => pathname.includes(endpoint));
};

const getTenantUuidFromStorage = () => {
  try {
    return localStorage.getItem(CURRENT_TENANT_UUID_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
};

const getOrganizationUuidFromStorage = () => {
  try {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const user = JSON.parse(userJson) as API.CurrentUser;
      return (
        user.organizationUuid || user.lastLoginOrganizationUuid || undefined
      );
    }
  } catch {
    // ignore
  }
  return undefined;
};

const hasAuthorizationHeader = (headers: RequestOptions['headers']) => {
  if (!headers) return false;
  const normalizedHeaders = headers as Record<string, unknown> & {
    get?: (name: string) => unknown;
  };
  const headerFromGetter =
    typeof normalizedHeaders.get === 'function'
      ? normalizedHeaders.get('Authorization')
      : undefined;
  return Boolean(
    normalizedHeaders.Authorization ||
      normalizedHeaders.authorization ||
      headerFromGetter,
  );
};

const handleUnauthorized = () => {
  clearOAuth2Tokens();
  if (
    history.location.pathname !== LOGIN_PATH &&
    history.location.pathname !== '/callback'
  ) {
    redirectToSSO();
  } else if (history.location.pathname !== LOGIN_PATH) {
    history.push(LOGIN_PATH);
  }
};

const shouldHandleUnauthorized = (error: RequestErrorLike) =>
  isManagedOAuthRequest(error.config?.url || error.response?.config?.url, [
    BizAppConfig.appApiUrl,
    process.env.UMI_APP_AUTH_BFF_URL,
  ]);

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, msg } =
        res as unknown as ResponseStructure;
      if (success === false) {
        const error: any = new Error(msg || errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage: msg || errorMessage, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: RequestErrorLike, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      if (error.name === 'AuthSessionUnavailableError') throw error;

      const normalized = normalizeRequestError(error);
      const errorCode =
        error.response?.data?.code || (error as any).info?.errorCode;

      if (errorCode === 10010) {
        appMessage.error('租户环境已变更，系统将自动刷新');
        setTimeout(() => {
          localStorage.clear();
          window.location.reload();
        }, 1500);
        throw error;
      }

      if (normalized.status === 401 && shouldHandleUnauthorized(error)) {
        handleUnauthorized();
        throw error;
      }

      if (error.response) {
        appMessage.error(normalized.message);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        appMessage.error('网络错误，请检查网络连接后重试');
      } else {
        appMessage.error(normalized.message);
      }

      error.handled = true;
      throw error;
    },
  },

  // 请求拦截器
  requestInterceptors: [
    async (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      const requestUrl = config?.url as string | undefined;
      const skipTokenRefresh = matchesAuthEndpoint(
        requestUrl,
        AUTH_ENDPOINTS_SKIP_REFRESH,
      );
      const skipAuthHeader = matchesAuthEndpoint(
        requestUrl,
        AUTH_ENDPOINTS_SKIP_AUTH_HEADER,
      );
      if (skipAuthHeader) {
        return config;
      }

      const headers = { ...config.headers };
      const token = skipTokenRefresh
        ? getAccessToken()
        : hasAuthorizationHeader(config.headers)
          ? null
          : await ensureValidAccessToken();
      const hasProvidedToken = hasAuthorizationHeader(config.headers);
      const requiresManagedSession = isManagedOAuthRequest(requestUrl, [
        BizAppConfig.appApiUrl,
        process.env.UMI_APP_AUTH_BFF_URL,
      ]);
      const allowsMissingToken = matchesAuthEndpoint(
        requestUrl,
        AUTH_ENDPOINTS_ALLOW_MISSING_TOKEN,
      );
      if (
        requiresManagedSession &&
        !token &&
        !hasProvidedToken &&
        !allowsMissingToken
      ) {
        const error = new Error('当前登录会话不可用，无法发送认证请求');
        error.name = 'AuthSessionUnavailableError';
        throw error;
      }
      const tenantUuid = getTenantUuidFromStorage();
      const organizationUuid = getOrganizationUuidFromStorage();

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      // 仅将当前选择租户作为请求提示；租户归属和授权必须由后端通过 IAM 验证。
      if (tenantUuid) {
        headers['x-tenant-id'] = tenantUuid;
      }
      if (organizationUuid) {
        headers['x-organization-id'] = organizationUuid;
      }

      config.headers = headers;
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const responseData = (response as { data?: unknown }).data;

      if ((responseData as { success?: boolean } | undefined)?.success === false) {
        appMessage.error('请求失败！');
      }
      (response as { data?: unknown }).data = unwrapBffResponse(responseData);
      return response;
    },
  ],
};
